import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { validateDateParam } from '../lib/validation';

const urgencyLabel = {
  normal: 'Normal',
  high: 'HIGH PRIORITY',
  critical: 'CRITICAL',
};

const urgencyPrintStyle = {
  normal: '',
  high: 'border-2 border-orange-500',
  critical: 'border-2 border-red-500 bg-red-50',
};

function PrintView() {
  const { date } = useParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState('crew'); // 'crew' or 'management'

  useEffect(() => {
    let mounted = true;
    const fetchJobs = async () => {
      if (!validateDateParam(date)) {
        if (mounted) setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id, urgency_flag, unit_number, job_type_text, special_notes,
          daily_sort_order, deadline_date,
          properties (id, name, address, city, state, zip, gate_code, access_notes, property_manager_name, property_manager_phone),
          job_tasks (id, task_description, sort_order)
        `)
        .eq('scheduled_date', date)
        .eq('is_deleted', false)
        .order('daily_sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });

      if (!mounted) return;

      if (!error && data) {
        const sortedJobs = data.map((job) => ({
          ...job,
          job_tasks: (job.job_tasks || []).sort((a, b) => a.sort_order - b.sort_order),
        }));
        setJobs(sortedJobs);
      }
      setLoading(false);
    };

    fetchJobs();
    return () => { mounted = false; };
  }, [date]);

  const jobsByProperty = jobs.reduce((acc, job) => {
    const key = job.properties?.name || 'Unknown Property';
    (acc[key] = acc[key] || []).push(job);
    return acc;
  }, {});

  const jobNumbers = new Map(jobs.map((job, i) => [job.id, i + 1]));

  const renderJobCard = (job, jobNum, addPageBreak) => {
    const property = job.properties;
    const tasks = job.job_tasks || [];
    const fullAddress = property
      ? [
          property.address,
          [property.city, property.state, property.zip].filter(Boolean).join(', '),
        ]
          .filter(Boolean)
          .join(', ')
      : '';

    return (
      <div
        key={job.id}
        className={`border rounded-lg p-4 print:p-3 print:break-inside-avoid ${
          printMode === 'crew' ? 'job-card-print-crew' : 'job-card-print-management'
        } ${addPageBreak ? 'print:break-after-page' : ''} ${urgencyPrintStyle[job.urgency_flag] || ''}`}
      >
        <div className="flex justify-between items-start mb-3 print:mb-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-medium">Job {jobNum}</span>
              {job.urgency_flag !== 'normal' && (
                <span
                  className={`px-2 py-0.5 text-xs font-bold rounded ${
                    job.urgency_flag === 'critical'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {urgencyLabel[job.urgency_flag]}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 print:text-lg">
              {property?.name || 'Unknown Property'}
              {job.unit_number && (
                <span className="font-normal text-gray-600"> - Unit {job.unit_number}</span>
              )}
            </h2>
            {job.job_type_text && (
              <p className="text-sm text-gray-600 font-medium">{job.job_type_text}</p>
            )}
          </div>
          {job.deadline_date && (
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase">Deadline</p>
              <p className="text-sm font-medium text-gray-700">
                {new Date(job.deadline_date + 'T00:00:00').toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {fullAddress && (
          <div className="mb-3 print:mb-2">
            <p className="text-xs text-gray-500 uppercase font-medium">Address</p>
            <p className="text-sm text-gray-800">{fullAddress}</p>
          </div>
        )}

        {(property?.gate_code || property?.access_notes) && (
          <div className="mb-3 print:mb-2 bg-yellow-50 p-2 rounded border border-yellow-200 print:bg-transparent">
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Access Information</p>
            {property.gate_code && (
              <p className="text-sm text-gray-800">
                <span className="font-medium">Gate Code:</span> {property.gate_code}
              </p>
            )}
            {property.access_notes && (
              <p className="text-sm text-gray-800">{property.access_notes}</p>
            )}
          </div>
        )}

        {(property?.property_manager_name || property?.property_manager_phone) && (
          <div className="mb-3 print:mb-2">
            <p className="text-xs text-gray-500 uppercase font-medium">Property Manager</p>
            <p className="text-sm text-gray-800">
              {property.property_manager_name}
              {property.property_manager_phone && (
                <span className="ml-2 text-gray-600">({property.property_manager_phone})</span>
              )}
            </p>
          </div>
        )}

        {tasks.length > 0 && (
          <div className="mb-3 print:mb-2">
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Task Checklist</p>
            <ul className="space-y-1">
              {tasks.map((task) => (
                <li key={task.id} className="flex items-start text-sm">
                  <span className="w-4 h-4 border-2 border-gray-400 rounded mr-2 mt-0.5 flex-shrink-0 print:border-black" />
                  <span className="text-gray-800">{task.task_description}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {job.special_notes && (
          <div className="border-t pt-2 print:pt-1">
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Special Notes</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{job.special_notes}</p>
          </div>
        )}
      </div>
    );
  };

  const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Screen-only header */}
      <div className="print:hidden bg-brand-black p-4 border-b sticky top-0">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <Link to={`/schedule?date=${date}`} className="text-gray-300 hover:text-white">
              ← Back to Schedule
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* Print Mode Toggle */}
            <div className="flex rounded-lg border border-gray-600 overflow-hidden">
              <button
                onClick={() => setPrintMode('crew')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  printMode === 'crew'
                    ? 'bg-brand-coral text-white'
                    : 'bg-transparent text-gray-300 hover:bg-gray-700'
                }`}
              >
                For Crew
              </button>
              <button
                onClick={() => setPrintMode('management')}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-600 transition-colors ${
                  printMode === 'management'
                    ? 'bg-brand-coral text-white'
                    : 'bg-transparent text-gray-300 hover:bg-gray-700'
                }`}
              >
                For Management
              </button>
            </div>
            <button
              onClick={handlePrint}
              className="bg-brand-coral text-white px-6 py-2 rounded-md hover:bg-brand-coral-dark"
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Printable content */}
      <div className="max-w-4xl mx-auto p-6 print:p-0 print:max-w-none">
        {/* Header - screen only */}
        <div className="text-center mb-8 print:hidden">
          <h1 className="text-2xl font-bold text-gray-900">
            Daily Work Schedule
          </h1>
          <p className="text-lg text-gray-600">{formattedDate}</p>
          <p className="text-sm text-gray-500 mt-1">
            {jobs.length} job{jobs.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>

        {jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No jobs scheduled for this day.
          </div>
        ) : printMode === 'crew' ? (
          <div className="space-y-6 print:space-y-4">
            {Object.values(jobsByProperty).flat().map((job, index, arr) =>
              renderJobCard(job, jobNumbers.get(job.id), index < arr.length - 1)
            )}
          </div>
        ) : (
          <div className="space-y-8 print:space-y-4">
            {Object.entries(jobsByProperty).map(([propName, propJobs]) => (
              <div key={propName} className="print:break-inside-avoid-page">
                <div className="flex items-baseline gap-2 border-b-2 border-gray-300 pb-1 mb-3">
                  <h3 className="text-base font-bold text-gray-800 uppercase tracking-wide">
                    {propName}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {propJobs.length} {propJobs.length === 1 ? 'job' : 'jobs'}
                  </span>
                </div>
                <div className="space-y-4">
                  {propJobs.map((job) => renderJobCard(job, jobNumbers.get(job.id), false))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default PrintView;
